import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import "./dashboard.css";

const ACCESS_KEY = "rladashboard165";
const EXPIRY_MINUTES = 30;

const Dashboard = () => {
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessInput, setAccessInput] = useState("");
  const [accessError, setAccessError] = useState("");
  const [data, setData] = useState([]);
  const [eventDates, setEventDates] = useState([]);
  const [filters, setFilters] = useState({
    namaLengkap: "",
    kategori: "",
    namaTim: "",
    universitas: "",
    peran: "",
    gender: "",
    email: "",
    hp: "",
  });

  // 🔐 Load akses dari localStorage
  useEffect(() => {
    const savedAccess = localStorage.getItem("dashboard_access");
    const savedTime = localStorage.getItem("dashboard_access_time");
    if (savedAccess && savedTime) {
      const elapsed = (Date.now() - parseInt(savedTime)) / 60000;
      if (elapsed < EXPIRY_MINUTES && savedAccess === ACCESS_KEY) {
        setAccessGranted(true);
        fetchData();
      } else {
        localStorage.removeItem("dashboard_access");
        localStorage.removeItem("dashboard_access_time");
      }
    }
  }, []);

  // 🧾 Fetch data API
  const fetchData = async () => {
    try {
      const res = await axios.get(
        "https://tiket.rlagency.id/api/get_dashboard_data.php"
      );
      if (res.data.success) {
        setData(res.data.data || []);
        setEventDates(res.data.event_dates || []);
      }
    } catch (err) {
      console.error("Gagal memuat data:", err);
    }
  };

  // 🔓 Verifikasi akses
  const handleAccess = () => {
    if (accessInput === ACCESS_KEY) {
      localStorage.setItem("dashboard_access", ACCESS_KEY);
      localStorage.setItem("dashboard_access_time", Date.now().toString());
      setAccessGranted(true);
      fetchData();
    } else {
      setAccessError("Akses anda ditolak");
    }
  };

  // 🔍 Filter
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const filteredData = data.filter((item) => {
    return Object.keys(filters).every((key) =>
      item[key]
        ?.toString()
        .toLowerCase()
        .includes(filters[key].toLowerCase())
    );
  });

  // 📊 Summary
  const totalPeserta = data.length;
  const totalHadirPerHari = {};
  eventDates.forEach((date) => {
    totalHadirPerHari[date] = data.filter(
      (d) => d.checkins?.[date] && d.checkins[date] !== "Tidak Hadir"
    ).length;
  });

  // 🕒 Format waktu +7 jam
  const formatTimePlus7 = (datetime, dateKey = null) => {
    if (!datetime) return "";
    let date;
    if (/^\d{2}:\d{2}/.test(datetime)) {
      // Jika hanya berupa jam (contoh: "07:09"), tambahkan tanggal dari key
      if (!dateKey) return datetime;
      date = new Date(`${dateKey}T${datetime}:00`);
    } else {
      date = new Date(datetime.replace(" ", "T"));
    }
    if (isNaN(date)) return datetime;
    date.setHours(date.getHours() + 7);
    return date.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // 📤 Export ke Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const exportData = data.map((d, i) => {
      const row = {
        No: i + 1,
        "Nama Lengkap": d.namaLengkap,
        Kategori: d.kategori,
        "Nama Tim": d.namaTim,
        Universitas: d.universitas,
        Peran: d.peran,
        Gender: d.gender,
        Email: d.email,
        HP: d.hp,
      };
      eventDates.forEach((date) => {
        row[date] = d.checkins?.[date]
          ? formatTimePlus7(d.checkins[date], date)
          : d.checkins?.[date] === "Tidak Hadir"
          ? "Tidak Hadir"
          : "";
      });
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(exportData);
    XLSX.utils.book_append_sheet(wb, ws, "Dashboard");
    XLSX.writeFile(wb, "rekap_dashboard.xlsx");
  };

  return (
    <div className="dashboard-page">
      {!accessGranted ? (
        <div className="access-modal">
          <div className="access-box">
            <h3>Masukkan Akses Key</h3>
            <input
              type="password"
              placeholder="Ketik akses key..."
              value={accessInput}
              onChange={(e) => setAccessInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAccess()}
            />
            {accessError && <p className="error-text">{accessError}</p>}
            <button onClick={handleAccess}>Masuk</button>
          </div>
        </div>
      ) : (
        <div className="dashboard-container">
          <div className="dashboard-header">
            <h2>📊 Dashboard Kehadiran Peserta</h2>
            <button onClick={exportToExcel}>⬇️ Export Excel</button>
          </div>

          {/* Summary */}
          <div className="summary-box">
            <div>Total Peserta: <strong>{totalPeserta}</strong></div>
            {eventDates.map((date) => (
              <div key={date}>
                {new Date(date).toLocaleDateString("id-ID", {
                  day: "numeric",
                  month: "short",
                })}: <strong>{totalHadirPerHari[date]}</strong> hadir
              </div>
            ))}
          </div>

          {/* Tabel */}
          <div className="table-container">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Nama Lengkap</th>
                  <th>Kategori</th>
                  <th>Nama Tim</th>
                  <th>Universitas</th>
                  <th>Peran</th>
                  <th>Gender</th>
                  <th>Email</th>
                  <th>HP</th>
                  {eventDates.map((date) => (
                    <th key={date}>
                      {new Date(date).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                      })}
                    </th>
                  ))}
                </tr>
                <tr className="filter-row">
                  <th></th>
                  {Object.keys(filters).map((key) => (
                    <th key={key}>
                      <input
                        type="text"
                        value={filters[key]}
                        onChange={(e) =>
                          handleFilterChange(key, e.target.value)
                        }
                        placeholder="Filter..."
                      />
                    </th>
                  ))}
                  {eventDates.map((d) => (
                    <th key={d}></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((item, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{item.namaLengkap}</td>
                      <td>{item.kategori}</td>
                      <td>{item.namaTim}</td>
                      <td>{item.universitas}</td>
                      <td>{item.peran}</td>
                      <td>{item.gender}</td>
                      <td>{item.email}</td>
                      <td>{item.hp}</td>
                      {eventDates.map((date) => {
                        const val = item.checkins?.[date];
                        const displayVal = val
                          ? formatTimePlus7(val, date)
                          : val === "Tidak Hadir"
                          ? "Tidak Hadir"
                          : "";
                        return (
                          <td
                            key={date}
                            className={
                              val === "Tidak Hadir"
                                ? "td-absent"
                                : val
                                ? "td-present"
                                : ""
                            }
                          >
                            {displayVal}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9 + eventDates.length} className="no-data">
                      Tidak ada data ditemukan
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
